'use client'

import { useEffect, useRef } from 'react'
import { useSession } from 'next-auth/react'
import { describeShiftNode, deviceClass, pendingVitals, takePriorLoads, type Vitals } from '@/lib/rum/beacon'

/**
 * REAL-VISITOR WEB VITALS (register rum-web-vitals-1101). Measures LCP / FCP / INP / CLS / TTFB on the visitor's
 * own device for each HARD page load and beacons them to /api/rum, so "did the perf work make pages faster for
 * people" has a visitor-side answer, read per day in /admin/performance.
 *
 * ⚡ OFF THE CRITICAL PATH: web-vitals (2.4KB brotli) is import()ed in an effect, after hydration, as its own
 *   chunk. Its PerformanceObservers are buffered, so LCP, FCP, CLS and TTFB are still captured from the start of
 *   the load; only interactions before it registers are missing from INP. Next's own useReportWebVitals also
 *   registers in an effect, so this is the same coverage without adding the library to the layout chunk.
 * ⚠ ONE ROW PER HARD LOAD. Mounted in the root layout, which does not remount on a client navigation, so the row
 *   is the landing route; INP and CLS accumulate over the visit from there (web-vitals behaviour in an SPA).
 * ⚠ AUTOMATION IS SKIPPED here (navigator.webdriver: Playwright, Puppeteer, our daily cwv probe) and again on
 *   the server by user agent, or our own checks would be counted as visitors.
 */
export default function WebVitalsReporter() {
  const { status } = useSession()
  const signedIn = useRef<boolean | undefined>(undefined)
  if (status === 'authenticated') signedIn.current = true
  else if (status === 'unauthenticated') signedIn.current = false

  useEffect(() => {
    if (typeof window === 'undefined' || navigator.webdriver || typeof navigator.sendBeacon !== 'function') return
    let cancelled = false
    const id = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : null
    if (!id) return
    const path = window.location.pathname
    const device = deviceClass(window.innerWidth)
    const conn = (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType
    const current: Vitals = {}
    const sent: Vitals = {}
    let nav: string | undefined
    // Read and bumped ONCE per hard load, before any beacon (register rum-proxy-flood-rows-in-web-vitals-1103).
    let storage: Storage | null = null
    try { storage = window.localStorage } catch { /* blocked: sent as null, never as 0 */ }
    const pl = takePriorLoads(storage)

    // CLS ATTRIBUTION (register rum-cls-attribution-1103): the largest single shift, its element (describeShiftNode:
    // tag/#id/classes, no text) and ms since the last tap or key. "Since input" separates a click whose content
    // lands > 500ms later (counted, unlike the first 500ms) from a shift nobody caused. Buffered, so load shifts count.
    let lastInput = -1
    const onInput = () => { lastInput = performance.now() }
    let shift: { t: string; v: number; i: number | null } | null = null
    let po: PerformanceObserver | null = null
    try {
      po = new PerformanceObserver((list) => {
        for (const e of list.getEntries() as (PerformanceEntry & { value: number; hadRecentInput: boolean; sources?: { node?: Node | null }[] })[]) {
          if (e.hadRecentInput || (shift && e.value <= shift.v)) continue
          const t = describeShiftNode((e.sources || []).map((s) => s.node).find(Boolean) as never)
          if (t) shift = { t, v: e.value, i: lastInput < 0 ? null : Math.round(e.startTime - lastInput) }
        }
      })
      po.observe({ type: 'layout-shift', buffered: true })
    } catch { po = null /* no layout-shift support (Safari): no attribution, CLS itself is unaffected */ }
    window.addEventListener('pointerdown', onInput, { capture: true, passive: true })
    window.addEventListener('keydown', onInput, { capture: true, passive: true })

    const flush = () => {
      const m = pendingVitals(current, sent)
      if (!m) return
      const body = JSON.stringify({ id, path, m, nav, device, conn, signedIn: signedIn.current, build: process.env.NEXT_PUBLIC_BUILD_ID, shift: shift ?? undefined, pl: pl ?? undefined })
      try {
        if (navigator.sendBeacon('/api/rum', new Blob([body], { type: 'application/json' }))) Object.assign(sent, m)
      } catch { /* best effort: a lost beacon must never affect the page */ }
    }
    const onVisibility = () => { if (document.visibilityState === 'hidden') flush() }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', onVisibility)

    import('next/dist/compiled/web-vitals')
      .then(({ onCLS, onFCP, onINP, onLCP, onTTFB }) => {
        if (cancelled) return
        const report = (metric: { name: string; value: number; navigationType?: string }) => {
          if (metric.name !== 'TTFB' && metric.name !== 'FCP' && metric.name !== 'LCP' && metric.name !== 'INP' && metric.name !== 'CLS') return
          current[metric.name] = metric.value
          if (!nav && metric.navigationType) nav = metric.navigationType
          // Finalised after our hide flush (INP / CLS on hide): send the late value now, while the page can.
          if (document.visibilityState === 'hidden') flush()
        }
        onTTFB(report); onFCP(report); onLCP(report); onCLS(report); onINP(report)
      })
      .catch(() => { /* chunk failed to load: no measurement, never an error on the page */ })

    return () => {
      cancelled = true
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pointerdown', onInput, { capture: true })
      window.removeEventListener('keydown', onInput, { capture: true })
      po?.disconnect()
    }
  }, [])

  return null
}
